import { Link } from 'react-router-dom'
import { ArrowUpRight, CornerDownRight, RotateCcw } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  assignmentHasDeclineChain,
  buildAssignmentDetailsPath,
  isDeclinedAssignmentStatus,
  resolveAssignmentReference,
} from '@/lib/assignment-decline-chain'
import { withReturnTo } from '@/lib/return-navigation'
import { withServiceInstanceId } from '@/lib/service-instance'
import { formatDateTimeForLocale } from '@/lib/org-date-locale'
import type { Assignment } from '@/types'

type AssignmentDeclineChainProps = {
  assignment: Assignment
  serviceInstanceId?: string | null
  returnPath?: string
  variant?: 'compact' | 'detail'
  locale?: string
  timeZone?: string
  className?: string
}

function buildAssignmentLink(
  assignmentId: string | number,
  serviceInstanceId?: string | null,
  returnPath?: string,
) {
  const path = withServiceInstanceId(buildAssignmentDetailsPath(assignmentId), serviceInstanceId)
  return returnPath ? withReturnTo(path, returnPath) : path
}

export function AssignmentDeclineChainIndicator({
  assignment,
  serviceInstanceId,
  returnPath,
  variant = 'compact',
  locale,
  timeZone,
  className,
}: AssignmentDeclineChainProps) {
  if (!assignmentHasDeclineChain(assignment)) {
    return null
  }

  const priorAttemptId = assignment.parent_assignment
  const priorAttemptRef = resolveAssignmentReference(priorAttemptId, assignment.parent_assignment_ref)
  const openSlotId = assignment.follow_up_assignment_id
  const openSlotRef = resolveAssignmentReference(openSlotId, assignment.follow_up_assignment_ref)
  const declinedBy = assignment.declined_worker_name
    || (assignment.declined_worker ? `Worker #${assignment.declined_worker}` : null)
  const reason = assignment.rejection_reason?.trim()
  const isDeclined = isDeclinedAssignmentStatus(assignment.status)
  const rejectedAtLabel = assignment.rejected_at
    ? formatDateTimeForLocale(assignment.rejected_at, locale, '-', timeZone)
    : null

  if (variant === 'detail') {
    return (
      <div className={cn('rounded-xl border border-border/70 bg-muted/20 p-4', className)}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-700">
            <RotateCcw className="h-4 w-4" />
          </div>
          <div className="min-w-0 space-y-3">
            <div>
              <p className="text-sm font-medium text-foreground">Reassignment history</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {isDeclined
                  ? 'This slot was declined and may have been reopened for another worker.'
                  : 'This open slot was created after a worker declined the same role.'}
              </p>
            </div>

            {isDeclined ? (
              <div className="rounded-lg border border-rose-200/80 bg-rose-50/70 px-3 py-2 text-sm">
                <p className="font-medium text-rose-900">
                  {declinedBy ? `${declinedBy} declined` : 'Worker declined'}
                  {rejectedAtLabel ? ` · ${rejectedAtLabel}` : ''}
                </p>
                {reason ? (
                  <p className="mt-1 text-rose-800/90">{reason}</p>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {priorAttemptId && priorAttemptRef ? (
                <Link
                  to={buildAssignmentLink(priorAttemptId, serviceInstanceId, returnPath)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <CornerDownRight className="h-3.5 w-3.5 text-muted-foreground" />
                  View declined attempt
                  <span className="text-muted-foreground">{priorAttemptRef}</span>
                  <ArrowUpRight className="h-3 w-3 text-muted-foreground" />
                </Link>
              ) : null}

              {openSlotId && openSlotRef ? (
                <Link
                  to={buildAssignmentLink(openSlotId, serviceInstanceId, returnPath)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-800 transition-colors hover:border-sky-300 hover:bg-sky-100"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  View open slot
                  <span>{openSlotRef}</span>
                  <ArrowUpRight className="h-3 w-3" />
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-1', className)}>
      {priorAttemptId && priorAttemptRef ? (
        <Link
          to={buildAssignmentLink(priorAttemptId, serviceInstanceId, returnPath)}
          className="inline-flex max-w-full items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary"
          title={`Opened after a decline · ${priorAttemptRef}`}
        >
          <RotateCcw className="h-3 w-3 shrink-0 text-amber-600" />
          <span className="truncate">After decline</span>
          <span className="truncate font-medium text-foreground/80">{priorAttemptRef}</span>
        </Link>
      ) : null}

      {openSlotId && openSlotRef ? (
        <Link
          to={buildAssignmentLink(openSlotId, serviceInstanceId, returnPath)}
          className="inline-flex max-w-full items-center gap-1 text-xs text-sky-700 transition-colors hover:text-sky-900"
          title={`Open slot available for round-robin · ${openSlotRef}`}
        >
          <CornerDownRight className="h-3 w-3 shrink-0" />
          <span className="truncate">Open slot</span>
          <span className="truncate font-medium">{openSlotRef}</span>
        </Link>
      ) : null}

      {isDeclined && (declinedBy || reason) ? (
        <p className="text-xs text-muted-foreground line-clamp-2" title={reason || undefined}>
          {declinedBy ? `${declinedBy} declined` : 'Declined'}
          {reason ? `: ${reason}` : ''}
        </p>
      ) : null}
    </div>
  )
}
